import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useState } from "react";
import {
  LayoutDashboard, Boxes, ShieldAlert, Network, Building2,
  Radar, FileText, Bell, LogOut, Sparkles, Radio, Settings as SettingsIcon, Zap, Terminal, Users, Search, Clock, Globe, Loader2, Scissors,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { usePermissions, ROLE_LABELS } from "@/lib/permissions";
import { useT } from "@/lib/i18n";
import { cn } from "@/lib/util";
import { useActiveFullScan } from "@/lib/useActiveFullScan";
import { AIAnalystPanel } from "./security/AIAnalystPanel";
import { CookieBanner } from "./CookieBanner";
import { LanguageToggle } from "./LanguageToggle";

// `action` filters items by RBAC matrix (see /lib/permissions.js).
// `hideForClient` hides items from the scoped-client role (they only see
// their own organization; showing the portfolio-wide "Clients" list would
// leak the existence of siblings).
const NAV_ITEMS = [
  { to: "/",              icon: Building2,       key: "nav.clients",        testid: "nav-clients",       hideForClient: true },
  { to: "/dashboard",     icon: LayoutDashboard, key: "nav.dashboard",      testid: "nav-dashboard" },
  { to: "/attack-surface",icon: Network,         key: "nav.attack_surface", testid: "nav-attack-surface" },
  { to: "/asset-discovery",icon: Search,         label: "Descubrimiento de Activos", testid: "nav-asset-discovery", adminOnly: true },
  { to: "/assets",        icon: Boxes,           key: "nav.assets",         testid: "nav-assets" },
  { to: "/findings",      icon: ShieldAlert,     key: "nav.findings",       testid: "nav-findings" },
  { to: "/third-party-risk", icon: Building2,    label: "Riesgo de Terceros", testid: "nav-third-party-risk" },
  { to: "/threat-intel",  icon: Radar,           key: "nav.threat_intel",   testid: "nav-threat-intel",  hideForClient: true },
  { to: "/mttr",          icon: Clock,           label: "MTTR",             testid: "nav-mttr",          hideForClient: true },
  { to: "/takedowns",     icon: Scissors,        label: "Takedowns",        testid: "nav-takedowns",     action: "scan.run", showForClient: true },
  { to: "/dns-analysis",  icon: Globe,           label: "Análisis DNS",     testid: "nav-dns-analysis",  hideForClient: true },
  { to: "/alert-rules",   icon: Zap,             key: "nav.alert_rules",    testid: "nav-alert-rules",   action: "alert_rules.manage" },
  { to: "/scanners",      icon: Terminal,        key: "nav.scanners",       testid: "nav-scanners",      action: "scan.run" },
  { to: "/reports",       icon: FileText,        key: "nav.reports",        testid: "nav-reports" },
  { to: "/users",         icon: Users,           label: "Usuarios",         testid: "nav-users",         action: "users.manage" },
  { to: "/settings",      icon: SettingsIcon,    key: "nav.settings",       testid: "nav-settings" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { can, role, isClient, organizationId } = usePermissions();
  const { t } = useT();
  const nav = useNavigate();
  const [aiOpen, setAiOpen] = useState(false);

  // For a client user, replace the "Clients (list)" item with a "My company"
  // shortcut that goes straight to their scoped organization detail page.
  const items = [...NAV_ITEMS];
  if (isClient && organizationId) {
    items.unshift({
      to: `/clients/${organizationId}`,
      icon: Building2,
      label: "Mi empresa",
      testid: "nav-my-company",
    });
  }
  const visibleNav = items.filter(n => {
    if (isClient && n.hideForClient) return false;
    if (n.adminOnly && role !== "admin") return false;
    if (n.action && !can(n.action) && !(isClient && n.showForClient)) return false;
    return true;
  });

  return (
    <div className="min-h-screen flex">
      <aside className="w-60 border-r border-border bg-card/40 flex flex-col shrink-0" data-testid="sidebar">
        <div className="p-5 border-b border-border">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 border border-primary/60 flex items-center justify-center bg-primary/10">
              <Radio className="w-4 h-4 text-primary" />
            </div>
            <div>
              <div className="font-display text-sm font-bold tracking-tight">CIBRAXIS</div>
              <div className="font-mono text-[9px] text-muted-foreground tracking-widest uppercase">{t("layout.brand_subtitle")}</div>
            </div>
          </div>
        </div>
        <nav className="flex-1 p-2 space-y-0.5 overflow-y-auto">
          {visibleNav.map((n) => (
            <NavLink
              key={n.to + (n.testid || "")}
              to={n.to}
              end={n.to === "/"}
              data-testid={n.testid}
              className={({ isActive }) => cn(
                "flex items-center gap-3 px-3 py-2 text-sm transition-colors",
                isActive
                  ? "bg-primary/10 text-primary border-l-2 border-primary"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted/40 border-l-2 border-transparent"
              )}
            >
              <n.icon className="w-4 h-4" />
              <span>{n.key ? t(n.key) : n.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-border space-y-2">
          <button
            onClick={() => setAiOpen(true)}
            className="w-full border border-primary/60 bg-primary/10 text-primary flex items-center gap-2 px-3 py-2 text-sm hover:bg-primary/20"
            data-testid="open-ai-analyst"
          >
            <Sparkles className="w-4 h-4" />
            <span className="font-display">{t("nav.ai_analyst")}</span>
          </button>
          <div className="border-t border-border pt-2">
            <div className="text-xs text-foreground">{user?.name}</div>
            <div className="font-mono text-[10px] text-muted-foreground tracking-wide">{user?.email}</div>
            <div className="font-mono text-[9px] text-primary tracking-widest uppercase mt-1" data-testid="sidebar-user-role">
              {ROLE_LABELS[role] || role}
            </div>
            <button
              onClick={logout}
              className="mt-2 w-full flex items-center gap-2 text-xs text-muted-foreground hover:text-sev-critical"
              data-testid="logout-btn"
            >
              <LogOut className="w-3 h-3" /> {t("layout.logout")}
            </button>
          </div>
        </div>
      </aside>

      <main className="flex-1 min-w-0 overflow-x-hidden">
        <CookieBanner />
        <div className="h-14 border-b border-border bg-background/80 backdrop-blur-md sticky top-0 z-30 flex items-center justify-between px-6">
          <div className="flex items-center gap-3">
            <div className="w-2 h-2 rounded-full bg-sev-low animate-pulse" />
            <span className="font-mono text-[10px] tracking-widest uppercase text-muted-foreground">
              {t("layout.live_monitoring")}
            </span>
          </div>
          <div className="flex items-center gap-4">
            <LanguageToggle />
            <button
              onClick={() => nav("/findings")}
              className="relative p-2 hover:bg-muted"
              data-testid="header-alerts"
            >
              <Bell className="w-4 h-4" />
              <span className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-sev-critical" />
            </button>
          </div>
        </div>
        <div className="p-6 md:p-8">
          {user?.license_expired && (
            <div className="mb-6 border border-sev-critical/60 bg-sev-critical/10 text-sev-critical px-4 py-3 text-sm"
                 data-testid="license-expired-banner">
              La licencia de tu empresa ha vencido. Tus datos se conservan, pero el acceso está bloqueado
              hasta que se renueve. Contacta con tu proveedor de servicios.
            </div>
          )}
          {children}
        </div>
      </main>

      <AIAnalystPanel open={aiOpen} onClose={() => setAiOpen(false)} />
      <FullScanFloatingBadge />
    </div>
  );
}

/**
 * Global "Full Scan en curso" badge — renders on every page while a Full-Scan
 * (single run OR multi-asset batch) is running. Clicking it navigates to
 * /scanners and requests the page to auto-open the progress modal.
 */
function FullScanFloatingBadge() {
  const [active] = useActiveFullScan();
  const nav = useNavigate();
  const loc = useLocation();
  if (!active) return null;
  const onClick = () => {
    if (loc.pathname === "/scanners") {
      // Same-page click: reopen via custom event (no flag needed).
      window.dispatchEvent(new CustomEvent("cibraxis:openFullScan"));
    } else {
      // Cross-page: set a transient flag so Scanners auto-opens on mount.
      sessionStorage.setItem("cibraxis:openFullScanOnMount", "1");
      nav("/scanners");
    }
  };
  return (
    <button
      type="button"
      onClick={onClick}
      className="fixed bottom-6 right-6 z-[100] bg-primary text-primary-foreground border border-primary/60 shadow-2xl px-4 py-3 font-mono text-xs tracking-widest uppercase inline-flex items-center gap-2 hover:bg-primary/90 animate-fade-in"
      data-testid="full-scan-floating-badge"
      title="Reabrir el progreso del Escaneo Completo en curso"
    >
      <Loader2 className="w-3.5 h-3.5 animate-spin" />
      Full Scan en curso · Ver progreso
    </button>
  );
}
